import type { CensusPatientSnapshot, CensusUnitPdfPayload } from '@/types/census-pdf';
import type {
    CensusPatientContact,
    CensusReportDetailItem,
    CensusReportDetailPatient,
    CensusReportDetailSection,
    ProgressNotePatientsDownloadResponse
} from '@/types/census';

const toStringOrNull = (value: unknown): string | null => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'string') return value.trim() || null;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    return null;
};

const toNumberOrNull = (value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
        const parsed = Number.parseFloat(value);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
};

const toYesNoOrNull = (value: unknown): string | null => {
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    const str = toStringOrNull(value)?.toLowerCase();
    if (!str) return null;
    if (str === 'true' || str === 'yes' || str === 'y') return 'Yes';
    if (str === 'false' || str === 'no' || str === 'n') return 'No';
    return null;
};

const parseJsonSafely = (value: string): unknown => {
    try {
        return JSON.parse(value);
    } catch {
        return null;
    }
};

const toStringArrayFromUnknown = (value: unknown): string[] => {
    if (!Array.isArray(value)) return [];
    return value.map((item) => toStringOrNull(item)).filter((item): item is string => Boolean(item));
};

const toBulletList = (value: unknown): string[] => {
    const text = toStringOrNull(value);
    if (!text) return [];

    if (text.startsWith('[')) {
        return toStringArrayFromUnknown(parseJsonSafely(text));
    }

    return text
        .split(/\r?\n|;|\|/)
        .map((item) => item.trim())
        .filter(Boolean);
};

const getLatestDateValue = (first: unknown, second: unknown): string | null => {
    const firstValue = toStringOrNull(first);
    const secondValue = toStringOrNull(second);

    if (!firstValue && !secondValue) {
        return null;
    }
    if (!firstValue) {
        return secondValue;
    }
    if (!secondValue) {
        return firstValue;
    }

    const firstTime = Date.parse(firstValue);
    const secondTime = Date.parse(secondValue);

    if (!Number.isNaN(firstTime) && !Number.isNaN(secondTime)) {
        return firstTime >= secondTime ? firstValue : secondValue;
    }

    if (firstValue >= secondValue) {
        return firstValue;
    }

    return secondValue;
};

const toBarriers = (value: unknown): { medical: string[]; nonMedical: string[] } => {
    const text = toStringOrNull(value);
    if (!text) {
        return { medical: [], nonMedical: [] };
    }

    if (text.startsWith('{')) {
        const parsed = parseJsonSafely(text);
        if (parsed && typeof parsed === 'object') {
            const record = parsed as Record<string, unknown>;
            return {
                medical: toStringArrayFromUnknown(record.medical),
                nonMedical: toStringArrayFromUnknown(record.nonMedical)
            };
        }
    }

    return { medical: toBulletList(text), nonMedical: [] };
};

const toContacts = (value: CensusReportDetailPatient['contacts']): CensusPatientContact[] => {
    if (!Array.isArray(value)) return [];

    return value
        .map((item) => {
            const contactType = toStringOrNull(item?.contactType) ?? '';
            const name = toStringOrNull(item?.name) ?? '';
            const phone = toStringOrNull(item?.phone) ?? '';
            if (!contactType && !name && !phone) return null;
            return { contactType, name, phone };
        })
        .filter((item): item is CensusPatientContact => item !== null);
};

const toAiDischargeReadiness = (
    value: CensusReportDetailPatient['aiDischargeReadiness']
): CensusPatientSnapshot['aiDischargeReadiness'] => {
    if (!value || typeof value !== 'object') {
        return null;
    }

    const tier = toStringOrNull(value.tier);
    const color = toStringOrNull(value.color);
    const medicalReadiness = toStringOrNull(value.medicalReadiness);
    if (!tier && !color && !medicalReadiness) {
        return null;
    }

    return { tier, color, medicalReadiness };
};

export const mapCensusDetailPatientToSnapshot = (patient: CensusReportDetailPatient): CensusPatientSnapshot => {
    const barriers = toBarriers(patient.barriersAi?.value);
    const payorsFallback = [toStringOrNull(patient.primaryInsurance), toStringOrNull(patient.secondaryInsurance)]
        .filter((value): value is string => Boolean(value))
        .join(' / ');

    return {
        patientId: toStringOrNull(patient.patientId),
        bedLabel: toStringOrNull(patient.roomBed) ?? '-',
        patientName: toStringOrNull(patient.patientName) ?? '-',
        fin: toStringOrNull(patient.fin) ?? '-',
        dob: toStringOrNull(patient.dob) ?? '-',
        age: toNumberOrNull(patient.age),
        sex: toStringOrNull(patient.gender),
        status: toStringOrNull(patient.encounterType),
        readmitRisk: toStringOrNull(patient.readmitRisk),
        returnWithin30Days: toStringOrNull(patient.return30dTranslation) ?? toYesNoOrNull(patient.return30d),
        reason: toStringOrNull(patient.reasonForVisit),
        disposition: toStringOrNull(patient.currentDispoPlan),
        attendingMd: toStringOrNull(patient.attendingMd),
        anticipatedDcDate: toStringOrNull(patient.anticipatedDcDt),
        dcOrderAt: toStringOrNull(patient.prepareDcDt),
        insurancePrimary: toStringOrNull(patient.primaryInsurance),
        insuranceSecondary: toStringOrNull(patient.secondaryInsurance),
        payors: (toStringOrNull(patient.payors) ?? payorsFallback) || null,
        ipLos: toNumberOrNull(patient.inpatientLos),
        los: toNumberOrNull(patient.totalLos),
        ampac: toNumberOrNull(patient.ampacScore),
        ipAdmitAt: toStringOrNull(patient.admitDt),
        obsAdmitAt: toStringOrNull(patient.obsOrderDt),
        molst: toStringOrNull(patient.molstDt),
        cnAssessment: toStringOrNull(patient.cnAssessment?.cnAssessmentDt),
        cnAssessmentNotes: toStringOrNull(patient.cnAssessment?.cnAdditionalNotes),
        lastPt: getLatestDateValue(patient.lastPtEvalNoteDt, patient.lastPtDailyNoteDt),
        lastOt: getLatestDateValue(patient.lastOtEvalNoteDt, patient.lastOtDailyNoteDt),
        imm: toStringOrNull(patient.immDt),
        moon: toStringOrNull(patient.moonDt),
        noon: toStringOrNull(patient.noonDt),
        notes: toStringOrNull(patient.patientNote?.note),
        patientNoteId: patient.patientNote?.id ?? null,
        firstNoteAuthor: toStringOrNull(patient.patientNote?.firstAuthor?.name),
        lastNoteAuthor: toStringOrNull(patient.patientNote?.lastEditor?.name),
        lastNoteEditedAt: toStringOrNull(patient.patientNote?.lastEditor?.date),
        barriersMedical: barriers.medical,
        barriersNonMedical: barriers.nonMedical,
        nextSteps: toBulletList(patient.nextStepsAi?.value),
        nextStepsSource: patient.nextStepsAi?.source ?? null,
        dischargePlanning: toStringOrNull(patient.dischargePlanningNotes),
        isWeekendDischarge: Boolean(patient.isWeekendDischarge),
        dischargeDetail: patient.dischargeDetail ?? null,
        contacts: toContacts(patient.contacts),
        aiDischargeReadiness: toAiDischargeReadiness(patient.aiDischargeReadiness)
    };
};

export const mapCensusDetailSectionToPayload = (section: CensusReportDetailSection): CensusUnitPdfPayload => ({
    unitRoomGroup: section.unitRoomGroup,
    generatedAt: section.snapshotTakenAt,
    lastSyncDate: section.lastSyncDate,
    patientCount: section.patientCount,
    patients: section.patients.map(mapCensusDetailPatientToSnapshot)
});

const compareCoverageSectionsByUnitRange = (left: CensusReportDetailSection, right: CensusReportDetailSection): number => {
    const unitCompare = (left.unit ?? '').localeCompare(right.unit ?? '', undefined, { numeric: true, sensitivity: 'base' });
    if (unitCompare !== 0) return unitCompare;

    const rangeCompare = (left.unitRoomGroup ?? '').localeCompare(right.unitRoomGroup ?? '', undefined, {
        numeric: true,
        sensitivity: 'base'
    });
    if (rangeCompare !== 0) return rangeCompare;

    return (left.unitRangeId ?? '').localeCompare(right.unitRangeId ?? '');
};

const getLatestSnapshotTakenAt = (sections: CensusReportDetailSection[]): string =>
    sections.reduce(
        (latest, section) => (section.snapshotTakenAt > latest ? section.snapshotTakenAt : latest),
        sections[0]?.snapshotTakenAt ?? ''
    );

const getLatestLastSyncDate = (sections: CensusReportDetailSection[]): string | null =>
    sections.reduce<string | null>((latest, section) => {
        const value = section.lastSyncDate;
        if (!value) {
            return latest;
        }
        if (!latest) {
            return value;
        }
        return value > latest ? value : latest;
    }, null);

/**
 * Coverage patients are ordered by unitRange, then packed 3-per-page across ranges.
 * Regular census ranges stay one payload per unit so paging and headers remain unit-scoped.
 */
const mapCoverageSectionsToPayload = (sections: CensusReportDetailSection[]): CensusUnitPdfPayload | null => {
    if (sections.length === 0) {
        return null;
    }

    const sortedSections = [...sections].sort(compareCoverageSectionsByUnitRange);
    const patients = sortedSections.flatMap((section) => section.patients.map(mapCensusDetailPatientToSnapshot));
    if (patients.length === 0) {
        return null;
    }

    return {
        unitRoomGroup: 'Coverage Patients',
        generatedAt: getLatestSnapshotTakenAt(sortedSections),
        lastSyncDate: getLatestLastSyncDate(sortedSections),
        patientCount: patients.length,
        patients,
        hideUnitHeader: true
    };
};

export const mapCensusDetailToSheetPayloads = (detail: CensusReportDetailItem): CensusUnitPdfPayload[] => {
    const rangePayloads = (detail.ranges ?? []).map(mapCensusDetailSectionToPayload);
    const coveragePayload = mapCoverageSectionsToPayload(detail.coverage ?? []);
    return coveragePayload ? [...rangePayloads, coveragePayload] : rangePayloads;
};

export const mapProgressNoteDownloadToSheetPayloads = (data: ProgressNotePatientsDownloadResponse): CensusUnitPdfPayload[] =>
    (data.ranges ?? []).map((range) => ({
        unitRoomGroup: range.unitRoomGroup,
        generatedAt: range.snapshotTakenAt,
        patientCount: range.patientCount,
        patients: range.patients.map(mapCensusDetailPatientToSnapshot)
    }));

export const applyCensusHospitalName = (payloads: CensusUnitPdfPayload[], hospitalName?: string | null): CensusUnitPdfPayload[] => {
    const name = hospitalName?.trim();
    if (!name) {
        return payloads;
    }
    return payloads.map((payload) => ({ ...payload, hospitalName: name }));
};
